'use client'

import { useCurrentBusinessProfileId } from '@/lib/contexts/BusinessProfileContext'
import { useIntegrationsModal } from '@/lib/contexts/IntegrationsModalContext'
import {
  getSidebarCollapsed,
  getSidebarCollapsedFromCookieString,
  updateSidebarCollapsed,
} from '@/lib/features/layoutSlice'
import { useGetScheduledTasksQuery } from '@/lib/features/scheduled-tasks/scheduledTasksApi'
import { useGetAllUserSessionsQuery } from '@/lib/features/session/sessionApi'
import {
  useSessionAwareUserQuery,
  useSessionAwareUserQuotaQuery,
} from '@/lib/features/user/userApi'
import { PAGE_PATH } from '@/lib/page-paths'
import { motion } from 'framer-motion'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FiPlus } from 'react-icons/fi'
import type { CopilotSession } from 'sprites-types/api/v2/copilot'
import { IntegrationsModal } from '../Integration/IntegrationsModal'
import { CalendlyButton } from './CalendlyButton'
import { SidebarFooter } from './SidebarFooter'
import { SidebarHeader } from './SidebarHeader'
import { NavItem, SidebarNav } from './SidebarNav'
import { ThreadList } from './ThreadList'
import { ThreadSearch } from './ThreadSearch'

type SidebarProps = {
  initialCookieString?: string
}

const getInitialCollapsedState = (cookieString?: string): boolean => {
  if (cookieString) {
    return getSidebarCollapsedFromCookieString(cookieString)
  }
  if (typeof window !== 'undefined') {
    return getSidebarCollapsed()
  }
  return false
}

export function Sidebar({ initialCookieString }: SidebarProps) {
  // Track permanent state (via click) vs temporary state (via hover)
  // Use initialCookieString during SSR/hydration to ensure server/client match
  const [isPermanentlyCollapsed, setIsPermanentlyCollapsed] = useState(() =>
    getInitialCollapsedState(initialCookieString)
  )

  // Sync with client-side cookies after hydration to handle any differences
  // Only run once after mount to avoid hydration mismatches
  // Note: isPermanentlyCollapsed is intentionally omitted from deps array
  // because we only want to sync once after mount, not on every state change
  useEffect(() => {
    if (initialCookieString === undefined && typeof window !== 'undefined') {
      const clientCollapsed = getSidebarCollapsed()
      if (clientCollapsed !== isPermanentlyCollapsed) {
        setIsPermanentlyCollapsed(clientCollapsed)
      }
    }
  }, [initialCookieString, isPermanentlyCollapsed])
  const [isHovering, setIsHovering] = useState(false)
  const [isDropdownHovering, setIsDropdownHovering] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const ignoreHoverRef = useRef(false)
  const hoverTimerRef = useRef<NodeJS.Timeout | null>(null)
  const [ignoreHover, setIgnoreHover] = useState(false)

  // Sidebar is collapsed if permanently collapsed AND (not hovering over sidebar or dropdown OR ignoreHover flag is set)
  const isCollapsed = isPermanentlyCollapsed && (!(isHovering || isDropdownHovering) || ignoreHover)

  const [shouldCloseDropdown, setShouldCloseDropdown] = useState(false)

  // Auto-close dropdown when mouse leaves both sidebar and dropdown (only if not permanently open)
  useEffect(() => {
    if (isPermanentlyCollapsed && !isHovering && !isDropdownHovering && isDropdownOpen) {
      // Faster dropdown close for better responsiveness
      const timer = setTimeout(() => {
        if (!isHovering && !isDropdownHovering) {
          setShouldCloseDropdown(true)
        }
      }, 50)
      return () => clearTimeout(timer)
    } else {
      setShouldCloseDropdown(false)
    }
  }, [isPermanentlyCollapsed, isHovering, isDropdownHovering, isDropdownOpen])

  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current)
      }
    }
  }, [])

  const [searchQuery, setSearchQuery] = useState('')
  const [totalLimit, setTotalLimit] = useState(50)
  const router = useRouter()
  const {
    isOpen: isIntegrationsModalOpen,
    openIntegrationsModal,
    closeIntegrationsModal,
  } = useIntegrationsModal()

  const { data: userData } = useSessionAwareUserQuery()
  const { data: quota } = useSessionAwareUserQuotaQuery()
  const currentBusinessProfileId = useCurrentBusinessProfileId()
  const workspaceId = userData?.workspace?.id

  const { data: scheduledTasksData } = useGetScheduledTasksQuery(
    { limit: 50 },
    { skip: !workspaceId }
  )
  const scheduledTasksUnseenCount = (scheduledTasksData?.tasks ?? []).reduce(
    (sum, t) => sum + t.unseenResultCount,
    0
  )

  const limitToUse = searchQuery ? 1000 : totalLimit

  const {
    data: sessionData,
    isFetching,
    isLoading,
  } = useGetAllUserSessionsQuery(
    {
      limit: limitToUse,
      offset: 0,
      includeInteractions: false,
      businessProfileId: currentBusinessProfileId || undefined,
    },
    { skip: !userData?.workspace?.id || !currentBusinessProfileId }
  )

  const user = userData?.user || null

  // Use sessionStorage to persist across navigation
  const [cachedSessions, setCachedSessions] = useState<CopilotSession[]>(() => {
    if (typeof window === 'undefined') return []
    try {
      const cached = sessionStorage.getItem('sidebar-sessions')
      return cached ? JSON.parse(cached) : []
    } catch {
      return []
    }
  })

  useEffect(() => {
    if (sessionData?.sessions && sessionData.sessions.length > 0) {
      setCachedSessions(sessionData.sessions)
      try {
        sessionStorage.setItem('sidebar-sessions', JSON.stringify(sessionData.sessions))
      } catch {
        // Ignore storage errors
      }
    }
  }, [sessionData])

  const sessions = sessionData?.sessions || cachedSessions
  const hasMore = sessionData?.pagination?.hasMore ?? false

  const filteredSessions = useMemo(() => {
    if (!searchQuery.trim()) return sessions
    const lowercaseQuery = searchQuery.toLowerCase()
    return sessions.filter(
      (session) =>
        session.id?.toLowerCase().includes(lowercaseQuery) ||
        session.threadSummary?.toLowerCase().includes(lowercaseQuery)
    )
  }, [sessions, searchQuery])

  const handleNavClick = useCallback(
    (item: NavItem) => {
      if (item.label === 'Integrations') {
        openIntegrationsModal()
      } else if (item.path) {
        router.push(item.path)
      }
    },
    [router, openIntegrationsModal]
  )

  const handleLoadMore = useCallback(() => {
    if (!isFetching) {
      setTotalLimit((prev) => prev + 50)
    }
  }, [isFetching])

  return (
    <>
      <motion.aside
        initial={false}
        animate={{ width: isCollapsed ? 64 : 280 }}
        transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
        className="font-hanken-grotesk relative flex h-full flex-col border-r border-gray-200/50 bg-white will-change-[width]"
        suppressHydrationWarning
        onMouseEnter={() => {
          // Only enable hover if not explicitly ignoring it
          if (!ignoreHoverRef.current) {
            // Reduced delay for more responsive expansion
            hoverTimerRef.current = setTimeout(() => {
              setIsHovering(true)
            }, 100)
          }
        }}
        onMouseLeave={() => {
          // Clear any pending hover timer
          if (hoverTimerRef.current) {
            clearTimeout(hoverTimerRef.current)
            hoverTimerRef.current = null
          }
          setIsHovering(false)
          // Don't reset isDropdownHovering here - it's tracked separately
          setIgnoreHover(false)
          ignoreHoverRef.current = false // Reset ignore flag when mouse leaves
        }}
      >
        <SidebarHeader
          isCollapsed={isCollapsed}
          isPermanentlyCollapsed={isPermanentlyCollapsed}
          isHovering={isHovering}
          onToggleCollapse={() => {
            const newCollapsed = !isPermanentlyCollapsed
            setIsPermanentlyCollapsed(newCollapsed)
            updateSidebarCollapsed(newCollapsed)
            // If closing (newCollapsed = true), immediately reset hover states to force collapse
            if (newCollapsed) {
              // Clear any pending hover timer
              if (hoverTimerRef.current) {
                clearTimeout(hoverTimerRef.current)
                hoverTimerRef.current = null
              }
              setIsHovering(false)
              setIsDropdownHovering(false)
              setIgnoreHover(true)
              ignoreHoverRef.current = true // Set ref to prevent hover from re-expanding
            }
          }}
        />

        <SidebarNav
          isCollapsed={isCollapsed}
          onNavClick={handleNavClick}
          badgeCounts={{ 'Scheduled Tasks': scheduledTasksUnseenCount }}
        />

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {!isCollapsed && (
            <>
              {/* New Chat Button */}
              <div className="border-b border-gray-200/50 p-3">
                <Link href={PAGE_PATH.COPILOT}>
                  <motion.div
                    className="text-ui-text hover:bg-ui-hover flex w-full items-center justify-center gap-2 rounded-2xl border border-gray-200 bg-white px-6 py-3 text-sm font-medium shadow-sm transition-colors"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <FiPlus className="h-4 w-4" />
                    New Chat
                  </motion.div>
                </Link>
              </div>

              <ThreadSearch value={searchQuery} onChange={setSearchQuery} />

              <ThreadList
                sessions={filteredSessions}
                searchQuery={searchQuery}
                hasMore={hasMore}
                isFetching={isFetching}
                isLoading={isLoading}
                onLoadMore={handleLoadMore}
              />
            </>
          )}
        </div>

        {/* Calendly Button */}
        <div className="border-t border-gray-200/50 p-3">
          <CalendlyButton isCollapsed={isCollapsed} />
        </div>

        <SidebarFooter
          user={user}
          quota={quota || null}
          isCollapsed={isCollapsed}
          onOpenIntegrations={openIntegrationsModal}
          onDropdownOpenChange={setIsDropdownOpen}
          onDropdownHoverChange={setIsDropdownHovering}
          shouldCloseDropdown={shouldCloseDropdown}
        />
      </motion.aside>

      <IntegrationsModal isOpen={isIntegrationsModalOpen} onClose={closeIntegrationsModal} />
    </>
  )
}
